// Service 18 — รับรองเอกสารเพื่อสมัครงานและสมัครทุนต่างประเทศ
// อ้างอิง: กรมการกงสุล กองสัญชาติและนิติกรณ์ (consular.mfa.go.th) — การรับรองเอกสารและรับรองคำแปล,
// กรมการจัดหางาน (doe.go.th) — การแจ้งการเดินทางไปทำงานต่างประเทศและการคุ้มครองคนหางาน,
// สำนักงาน ก.พ. (ocsc.go.th) — ทุนรัฐบาลและการรับรองคุณวุฒิ,
// สำนักงานปลัดกระทรวงการอุดมศึกษา วิทยาศาสตร์ วิจัยและนวัตกรรม (mhesi.go.th) — การรับรองคุณวุฒิการศึกษา,
// สภาวิชาชีพในประเทศไทย (แพทยสภา tmc.or.th, สภาการพยาบาล tnmc.or.th, สภาวิศวกร coe.or.th, สภาสถาปนิก act.or.th),
// NAATI (naati.com.au), VETASSESS (vetassess.com.au), Engineers Australia (engineersaustralia.org.au),
// ACS (acs.org.au), WES (wes.org), ENIC-NARIC (enic-naric.net),
// HCCH Apostille Convention (hcch.net) — มีผลกับประเทศไทยวันที่ 28 กุมภาพันธ์ 2570 (28 February 2027)
// Category definitions only — client-safe, no heavy FAQ payload.

export type EmploymentFaqCategory =
  | "work-experience-letter"
  | "degree-transcript-job"
  | "professional-license"
  | "scholarship-application"
  | "skills-assessment"
  | "police-clearance-job"
  | "translation-for-employer"
  | "mfa-apostille-job"
  | "embassy-attestation-gulf"
  | "timeline-and-rejection";

export interface EmploymentCategoryDef {
  slug: EmploymentFaqCategory;
  titleTh: string;
  titleEn: string;
  descTh: string;
  descEn: string;
  matchTh: RegExp;
  matchEn: RegExp;
}

export const EMPLOYMENT_FAQ_CATEGORIES: EmploymentCategoryDef[] = [
  {
    slug: "work-experience-letter",
    titleTh: "หนังสือรับรองการทำงานและประสบการณ์",
    titleEn: "Employment and work-experience letters",
    descTh:
      "การขอหนังสือรับรองการทำงาน สลิปเงินเดือน หนังสือรับรองตำแหน่งและระยะเวลาทำงาน การจัดรูปแบบให้ผู้ประเมินต่างประเทศยอมรับ และการรับรองลายมือชื่อผู้มีอำนาจลงนามก่อนส่งออกนอกประเทศ",
    descEn:
      "Obtaining employment certificates, payslips and letters stating position and dates, formatting them so overseas assessors accept them, and certifying the signature of the authorised signatory before the file leaves Thailand.",
    matchTh: /หนังสือรับรองการทำงาน|ประสบการณ์|สลิป|เงินเดือน|นายจ้าง|ตำแหน่ง|HR|ใบผ่านงาน/i,
    matchEn: /employment letter|work experience|payslip|employer|reference letter|HR|position|service certificate/i,
  },
  {
    slug: "degree-transcript-job",
    titleTh: "วุฒิการศึกษาและทรานสคริปต์เพื่อสมัครงาน",
    titleEn: "Degrees and transcripts for job applications",
    descTh:
      "การขอฉบับจริงและฉบับภาษาอังกฤษจากสถาบัน การรับรองคุณวุฒิ การส่งตรงถึงหน่วยประเมิน และความต่างระหว่างเอกสารสำหรับสมัครงานกับสำหรับสมัครเรียน",
    descEn:
      "Requesting originals and English-language copies from the institution, credential recognition, sending documents directly to an evaluator, and how job-application files differ from study-application files.",
    matchTh: /ปริญญา|ทรานสคริปต์|วุฒิ|สถาบัน|มหาวิทยาลัย|ใบรับรองผลการศึกษา|เกรด/i,
    matchEn: /degree|transcript|diploma|university|credential|academic record|GPA/i,
  },
  {
    slug: "professional-license",
    titleTh: "ใบอนุญาตประกอบวิชาชีพและใบรับรองสภาวิชาชีพ",
    titleEn: "Professional licences and council certificates",
    descTh:
      "ใบอนุญาตประกอบวิชาชีพเวชกรรม พยาบาล วิศวกรรม สถาปัตยกรรม บัญชี และครู การขอ Certificate of Good Standing จากสภาวิชาชีพ และการรับรองเพื่อยื่นต่อหน่วยงานกำกับในต่างประเทศ",
    descEn:
      "Medical, nursing, engineering, architecture, accounting and teaching licences, requesting a Certificate of Good Standing from the Thai professional council, and certifying it for a foreign regulator.",
    matchTh: /ใบอนุญาตประกอบวิชาชีพ|แพทยสภา|สภาการพยาบาล|สภาวิศวกร|สภาสถาปนิก|good standing|ใบประกอบ|คุรุสภา/i,
    matchEn: /professional licence|professional license|good standing|medical council|nursing council|engineer|architect|registration board/i,
  },
  {
    slug: "scholarship-application",
    titleTh: "เอกสารสมัครทุนรัฐบาลและทุนต่างประเทศ",
    titleEn: "Documents for government and overseas scholarships",
    descTh:
      "ชุดเอกสารสำหรับทุน ก.พ. ทุนรัฐบาลต่างประเทศ (Chevening, Fulbright, DAAD, MEXT, Australia Awards) หนังสือรับรองจากผู้บังคับบัญชา หนังสือค้ำประกัน และการรับรองคำแปลก่อนอัปโหลด",
    descEn:
      "Document sets for OCSC and foreign government scholarships (Chevening, Fulbright, DAAD, MEXT, Australia Awards), supervisor endorsements, guarantor undertakings and translation certification before upload.",
    matchTh: /ทุน|ก\.พ\.|Chevening|Fulbright|DAAD|MEXT|Australia Awards|ค้ำประกัน|ผู้บังคับบัญชา|ลาศึกษา/i,
    matchEn: /scholarship|OCSC|Chevening|Fulbright|DAAD|MEXT|Australia Awards|guarantor|study leave|sponsor/i,
  },
  {
    slug: "skills-assessment",
    titleTh: "การประเมินทักษะและคุณวุฒิเพื่อย้ายถิ่นทำงาน",
    titleEn: "Skills and credential assessment for work migration",
    descTh:
      "VETASSESS, Engineers Australia, ACS, TRA, WES, ENIC-NARIC และหน่วยประเมินอื่น ๆ ว่าต้องการเอกสารอะไร ต้องเป็นคำแปลแบบใด และต้องส่งจากใคร",
    descEn:
      "What VETASSESS, Engineers Australia, ACS, TRA, WES, ENIC-NARIC and similar bodies require: which documents, which type of translation, and who must send them.",
    matchTh: /VETASSESS|Engineers Australia|ACS|TRA|WES|ENIC|ประเมินทักษะ|ประเมินคุณวุฒิ|skills assessment/i,
    matchEn: /VETASSESS|Engineers Australia|ACS|TRA|WES|ENIC|skills assessment|credential evaluation/i,
  },
  {
    slug: "police-clearance-job",
    titleTh: "ใบตรวจประวัติอาชญากรรมสำหรับสมัครงาน",
    titleEn: "Police clearance certificates for employment",
    descTh:
      "การขอหนังสือรับรองความประพฤติจากกองทะเบียนประวัติอาชญากร สำนักงานตำรวจแห่งชาติ อายุการใช้งานที่นายจ้างและหน่วยงานกำกับยอมรับ และการรับรองต่อเนื่องที่กรมการกงสุล",
    descEn:
      "Requesting a police clearance certificate from the Police Clearance Service Centre, how long employers and regulators accept it, and the onward certification at the Department of Consular Affairs.",
    matchTh: /ตรวจประวัติ|อาชญากร|ความประพฤติ|police clearance|ลายพิมพ์นิ้วมือ|PCSC/i,
    matchEn: /police clearance|criminal record|good conduct|fingerprint|PCSC|background check/i,
  },
  {
    slug: "translation-for-employer",
    titleTh: "คำแปลที่นายจ้างและหน่วยงานปลายทางยอมรับ",
    titleEn: "Translations employers and destination bodies accept",
    descTh:
      "ความต่างระหว่างคำแปลรับรองโดยผู้แปล คำแปลที่กรมการกงสุลรับรอง NAATI สำหรับออสเตรเลีย/นิวซีแลนด์ และ sworn translator ในยุโรป รวมถึงการสะกดชื่อให้ตรงหนังสือเดินทาง",
    descEn:
      "The difference between a translator's certified translation, an MFA-certified translation, NAATI for Australia and New Zealand, and sworn translators in Europe — plus spelling names exactly as in the passport.",
    matchTh: /คำแปล|แปลเอกสาร|NAATI|sworn|รับรองคำแปล|สะกดชื่อ|ผู้แปล/i,
    matchEn: /translation|translator|NAATI|sworn|certified translation|spelling|name as in passport/i,
  },
  {
    slug: "mfa-apostille-job",
    titleTh: "นิติกรณ์กรมการกงสุลและ Apostille",
    titleEn: "MFA legalisation and the Apostille",
    descTh:
      "ลำดับชั้นการรับรองสำหรับแฟ้มสมัครงาน ตั้งแต่ต้นฉบับ คำแปล การรับรองที่กองสัญชาติและนิติกรณ์ ไปจนถึง Apostille ซึ่งอนุสัญญาจะมีผลกับประเทศไทยวันที่ 28 กุมภาพันธ์ 2570",
    descEn:
      "The certification chain for a job-application file — original, translation, certification by the Legalisation Division, and the Apostille, which enters into force for Thailand on 28 February 2027.",
    matchTh: /กรมการกงสุล|นิติกรณ์|Apostille|2570|แจ้งวัฒนะ|กองสัญชาติ/i,
    matchEn: /consular affairs|legalisation|legalization|apostille|2027|MFA/i,
  },
  {
    slug: "embassy-attestation-gulf",
    titleTh: "การรับรองสถานทูตและงานในตะวันออกกลาง",
    titleEn: "Embassy attestation and Gulf employment",
    descTh:
      "งานในสหรัฐอาหรับเอมิเรตส์ ซาอุดีอาระเบีย กาตาร์ คูเวต โอมาน และบาห์เรน ที่ต้องรับรองปริญญาบัตรผ่านสถานทูตปลายทางในไทยและกระทรวงการต่างประเทศของประเทศนั้น",
    descEn:
      "Jobs in the UAE, Saudi Arabia, Qatar, Kuwait, Oman and Bahrain that require degree attestation through the destination embassy in Thailand and that country's foreign ministry.",
    matchTh: /สถานทูต|เอมิเรตส์|ซาอุ|กาตาร์|คูเวต|โอมาน|บาห์เรน|attest|ตะวันออกกลาง/i,
    matchEn: /embassy|attestation|UAE|Saudi|Qatar|Kuwait|Oman|Bahrain|Gulf|MOFA/i,
  },
  {
    slug: "timeline-and-rejection",
    titleTh: "ระยะเวลา อายุเอกสาร และสาเหตุที่ถูกตีกลับ",
    titleEn: "Timelines, document age and rejection causes",
    descTh:
      "ระยะเวลาโดยทั่วไปของแต่ละขั้น อายุเอกสารที่นายจ้างและหน่วยประเมินยอมรับ และสาเหตุที่แฟ้มสมัครงานถูกตีกลับบ่อยที่สุดพร้อมวิธีป้องกัน",
    descEn:
      "Typical duration of each step, how recent documents must be for employers and assessors, and the most common reasons a job-application file is returned — with how to avoid them.",
    matchTh: /วันทำการ|ระยะเวลา|อายุเอกสาร|ตีกลับ|ปฏิเสธ|แก้ไข|เดือน|กี่วัน/i,
    matchEn: /working days|timeline|how long|validity|rejected|returned|resubmit|months/i,
  },
];

export function getEmploymentCategoryDef(slug: string): EmploymentCategoryDef | undefined {
  return EMPLOYMENT_FAQ_CATEGORIES.find((c) => c.slug === slug);
}